import { Button, Input, Label } from '@xhubio-saas/core-frontend/ui-components'
import { RefreshCw } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { ExtractResponse } from '@/api/pdfExtractClient'
import type { SellerProfile } from '@/api/sellerProfileClient'
import { AttachmentsSection } from './AttachmentsSection'
import type { ReviewAttachment } from './attachments'
import {
  type FieldTag,
  REVIEW_LIMITS,
  type ReviewField,
  type ReviewValues,
  sourceOf,
  tagFor,
  totalsConsistent,
} from './review'

const TAG_CLASS: Record<Exclude<FieldTag, null>, string> = {
  recognized: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200',
  profile: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-200',
  changed: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-200',
  missing: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200',
  add: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200',
}

function Tag({ tag, testId }: { tag: FieldTag; testId?: string }) {
  const { t } = useTranslation()
  if (!tag) return <span aria-hidden="true" />
  return (
    <span
      className={`inline-flex justify-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${TAG_CLASS[tag]}`}
      data-testid={testId}
      data-tag={tag}
    >
      {t(`invoiceFromPdf.review.tag.${tag}`)}
    </span>
  )
}

/** Base64 → Blob-URL fuer die Vorschau (wie `ConversionResultSection.tsx`), beim Verlassen wieder freigegeben. */
function usePdfUrl(base64: string): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let objectUrl: string | null = null
    try {
      const raw = atob(base64)
      const bytes = new Uint8Array(raw.length)
      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
      objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
      setUrl(objectUrl)
    } catch {
      setUrl(null)
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [base64])
  return url
}

export interface ReviewStepProps {
  pdf: string
  extract: ExtractResponse
  extracted: ReviewValues
  values: ReviewValues
  onChange: (field: ReviewField, value: string) => void
  /** Anlagen (E18) — zusaetzliche PDFs, die im Erzeugen-Aufruf mitreisen. */
  attachments: ReviewAttachment[]
  onAttachmentsChange: (next: ReviewAttachment[]) => void
  seller: SellerProfile | null
  onBack: () => void
  onNext: () => void
  running: boolean
  locale: string
}

export function ReviewStep({
  pdf,
  extract,
  extracted,
  values,
  onChange,
  attachments,
  onAttachmentsChange,
  seller,
  onBack,
  onNext,
  running,
  locale,
}: ReviewStepProps) {
  const { t } = useTranslation()
  const pdfUrl = usePdfUrl(pdf)
  const currency = extract.invoice.currency || 'EUR'
  const money = (n: number | null | undefined) =>
    typeof n === 'number'
      ? new Intl.NumberFormat(locale, { style: 'currency', currency }).format(n)
      : '—'
  const items = extract.invoice.items ?? []
  const itemsTag = items.length > 0 ? sourceOf(extract.fields, ['items']) : null
  const consistent = totalsConsistent(extract.checks)

  const row = (field: ReviewField, type: 'text' | 'date' | 'email' = 'text') => {
    const id = `invoice-from-pdf-${field}`
    const tag = tagFor(field, values[field], extracted, extract.fields)
    const needs = tag === 'missing' || tag === 'add'
    return (
      <div
        className="grid grid-cols-12 items-center gap-x-3 gap-y-1"
        key={field}
        data-testid={`invoice-from-pdf-row-${field}`}
      >
        <Label htmlFor={id} className="col-span-12 text-muted-foreground sm:col-span-3">
          {t(`invoiceFromPdf.review.field.${field}`)}
        </Label>
        <Input
          id={id}
          type={type}
          value={values[field]}
          maxLength={REVIEW_LIMITS[field]}
          placeholder={
            needs
              ? t(
                  tag === 'add'
                    ? 'invoiceFromPdf.review.notOnPdf'
                    : 'invoiceFromPdf.review.pleaseAdd'
                )
              : undefined
          }
          className={`col-span-9 h-9 sm:col-span-7 ${needs ? 'border-amber-400 bg-amber-50/60 dark:bg-amber-950/30' : ''}`}
          onChange={(e) => onChange(field, e.target.value)}
        />
        <div className="col-span-3 flex justify-end sm:col-span-2">
          <Tag tag={tag} testId={`invoice-from-pdf-tag-${field}`} />
        </div>
      </div>
    )
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!running) onNext()
  }

  const findings = extract.checks
  return (
    <form
      noValidate
      onSubmit={submit}
      className="space-y-4 rounded-xl border bg-card p-6 shadow-sm"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        {/* E16: das Original-PDF, ohne Markierung der Fundstellen — die Herkunft steht als Etikett je Feld. */}
        <div className="min-w-0">
          {pdfUrl ? (
            <iframe
              title={t('invoiceFromPdf.review.preview')}
              src={pdfUrl}
              className="block h-[70dvh] min-h-[420px] w-full rounded-md border border-border bg-muted"
              data-testid="invoice-from-pdf-preview"
            />
          ) : null}
        </div>

        <div className="min-w-0 space-y-6">
          <fieldset className="space-y-2">
            <legend className="mb-2 text-sm font-semibold">
              {t('invoiceFromPdf.review.group.invoice')}
            </legend>
            {row('invoiceNumber')}
            {row('issueDate', 'date')}
            {row('dueDate', 'date')}
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="mb-2 text-sm font-semibold">
              {t('invoiceFromPdf.review.group.buyer')}
            </legend>
            {row('buyerName')}
            {row('buyerStreet')}
            {row('buyerPostalCode')}
            {row('buyerCity')}
            {row('buyerEmail', 'email')}
            {row('buyerReference')}
          </fieldset>

          <section className="space-y-2" aria-labelledby="invoice-from-pdf-items-title">
            <div className="flex items-center justify-between gap-3">
              <h3 id="invoice-from-pdf-items-title" className="text-sm font-semibold">
                {t('invoiceFromPdf.review.group.items')}
              </h3>
              <Tag
                tag={items.length === 0 ? 'missing' : itemsTag === 'pdf' ? 'recognized' : null}
              />
            </div>
            <table className="w-full text-sm" data-testid="invoice-from-pdf-items">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-1 pr-2 font-medium">
                    {t('invoiceFromPdf.review.items.description')}
                  </th>
                  <th className="py-1 pr-2 text-right font-medium">
                    {t('invoiceFromPdf.review.items.net')}
                  </th>
                  <th className="py-1 text-right font-medium">
                    {t('invoiceFromPdf.review.items.vat')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: Positionen haben keine Kennung, die Reihenfolge ist die des PDF
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-1 pr-2 [overflow-wrap:anywhere]">{item.description}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{money(item.netAmount)}</td>
                    <td className="py-1 text-right tabular-nums">
                      {typeof item.taxRate === 'number' ? `${item.taxRate} %` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t">
                  <td className="py-1 pr-2 font-semibold">
                    {t('invoiceFromPdf.review.items.gross')}
                  </td>
                  <td className="py-1 text-right font-semibold tabular-nums" colSpan={2}>
                    <span data-testid="invoice-from-pdf-total">{money(extract.invoice.total)}</span>{' '}
                    <span
                      className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        consistent ? TAG_CLASS.recognized : TAG_CLASS.missing
                      }`}
                      data-testid="invoice-from-pdf-total-state"
                    >
                      {t(
                        consistent
                          ? 'invoiceFromPdf.review.totalOk'
                          : 'invoiceFromPdf.review.totalCheck'
                      )}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>

          <AttachmentsSection attachments={attachments} onChange={onAttachmentsChange} />

          <div className="grid grid-cols-12 items-center gap-x-3 gap-y-1">
            <span className="col-span-12 text-sm text-muted-foreground sm:col-span-3">
              {t('invoiceFromPdf.review.group.seller')}
            </span>
            <span className="col-span-9 text-sm sm:col-span-7 [overflow-wrap:anywhere]">
              {seller?.name}{' '}
              <Link to="/settings/company-data" className="underline underline-offset-4">
                {t('invoiceFromPdf.upload.editSeller')}
              </Link>
            </span>
            <div className="col-span-3 flex justify-end sm:col-span-2">
              <Tag tag="profile" />
            </div>
          </div>

          {findings.length > 0 && (
            <section
              className="space-y-1 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100"
              data-testid="invoice-from-pdf-checks"
            >
              <h3 className="font-medium">{t('invoiceFromPdf.review.checksTitle')}</h3>
              <ul className="list-inside list-disc space-y-0.5">
                {findings.map((c) => (
                  <li key={`${c.code}-${c.field ?? ''}-${c.message}`}>{c.message}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-3">
        <Button type="button" variant="outline" onClick={onBack}>
          {t('invoiceFromPdf.review.back')}
        </Button>
        <Button type="submit" disabled={running} data-testid="invoice-from-pdf-next">
          {running ? (
            <>
              <RefreshCw
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              {t('invoiceFromPdf.create.running')}
            </>
          ) : (
            t('invoiceFromPdf.review.next')
          )}
        </Button>
      </div>
    </form>
  )
}
